import { useState } from "react";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

import { formatLetterContent } from "../utils/formatContent";

const usePatientLetters = (patientId) => {
  const tenant = localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: ["patientLetters", tenant, patientId],
    enabled: !!patientId && !!tenant,
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenant}/patientletter/getbypatientid/${patientId}`,
        { params: { page: 1, rows: 100 } }
      );
      const result = response.data;

      return Array.isArray(result) ? result : (result?.data ?? []);
    },
  });
};

export const useInsertLetter = ({ patientId, onInsert }) => {
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");

  const { data: letters, isLoading } = usePatientLetters(patientId);

  // The endpoint normally returns a bare array, but can answer a non-array
  // (e.g. an error body delivered with 200) for some patients — guard so the
  // modal renders empty instead of crashing on `.filter`.
  const letterList = Array.isArray(letters) ? letters : [];

  const filtered = letterList.filter((letter) => {
    const term = search.toLowerCase();

    return (
      (letter.name || "").toLowerCase().includes(term) ||
      (letter.text || "").toLowerCase().includes(term)
    );
  });

  const handleInsert = (letter) => {
    onInsert(formatLetterContent(letter));
    setShowModal(false);
    setSearch("");
    toast.success("Letter added to email");
  };

  return {
    showModal,
    openModal: () => setShowModal(true),
    closeModal: () => {
      setShowModal(false);
      setSearch("");
    },
    search,
    setSearch,
    filtered,
    isLoading,
    handleInsert,
  };
};
